import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { DefectDataItem } from '../../types/DefectsDataItem';
import { DefectTabs } from '../../types/DefectTabs';
import { DefectInsightFilterBy } from '../../types/DefectInsightsData';
import { defectKey } from '../../utils/DefectIdentity';

export interface DefectCode {
    code: string;
    display_name?: string;
    category?: string;
}

export interface DefectState {
    data: DefectDataItem[];
    loading: boolean;
    error: boolean;
    filteredDefects: DefectDataItem[];
    activeTab: DefectTabs;
    showCloseDefectsDialog: boolean;
    showShareDefectsDialog: boolean;
    selectedVessel: string;
    insightsFilterBy: DefectInsightFilterBy;
    authorityMappings: Record<string, string[]>;
    defectCodes: DefectCode[];
}

export const initialState: DefectState = {
    data: [],
    loading: true,
    error: false,
    filteredDefects: [],
    activeTab: DefectTabs.summary,
    showCloseDefectsDialog: false,
    showShareDefectsDialog: false,
    selectedVessel: "",
    insightsFilterBy: DefectInsightFilterBy.VESSELS,
    authorityMappings: {},
    defectCodes: []
};

export const defectSlice = createSlice({
    name: 'defect',
    initialState,
    reducers: {
        setData: (state, action: PayloadAction<DefectDataItem[]>) => {
            state.data = action.payload;
        },
        setLoading: (state, action: PayloadAction<boolean>) => {
            state.loading = action.payload;
        },
        setError: (state, action: PayloadAction<boolean>) => {
            state.error = action.payload;
        },
        setFilteredDefects: (state, action: PayloadAction<DefectDataItem[]>) => {
            state.filteredDefects = action.payload;
        },
        setActiveTab: (state, action: PayloadAction<DefectTabs>) => {
            state.activeTab = action.payload;
        },
        setShowCloseDefectsDialog: (state, action: PayloadAction<boolean>) => {
            state.showCloseDefectsDialog = action.payload;
        },
        setShowShareDefectsDialog: (state, action: PayloadAction<boolean>) => {
            state.showShareDefectsDialog = action.payload;
        },
        setSelectedVessel: (state, action: PayloadAction<string>) => {
            state.selectedVessel = action.payload;
        },
        setInsightsFilterBy: (state, action: PayloadAction<DefectInsightFilterBy>) => {
            state.insightsFilterBy = action.payload;
        },
        updateData: (state, action: PayloadAction<DefectDataItem[]>) => {
            const updates = new Map(
                action.payload.map((defect) => [defectKey(defect), defect])
            );

            const applyUpdate = (defect: DefectDataItem) =>
                updates.get(defectKey(defect)) ?? defect;

            state.data = state.data.map(applyUpdate);
            state.filteredDefects = state.filteredDefects.map(applyUpdate);
        },
        setAuthorityMappings: (state, action: PayloadAction<Record<string, string[]>>) => {
            state.authorityMappings = action.payload;
        },
        setDefectCodes: (state, action: PayloadAction<DefectCode[]>) => {
            state.defectCodes = action.payload;
        }
    }
});

export const {
    setData,
    setLoading,
    setError,
    setFilteredDefects,
    setActiveTab,
    setShowCloseDefectsDialog,
    setShowShareDefectsDialog,
    setSelectedVessel,
    setInsightsFilterBy,
    updateData,
    setAuthorityMappings,
    setDefectCodes
} = defectSlice.actions;

export default defectSlice.reducer;

